import {
	Button,
	Checkbox,
	CircularProgress,
	FormControlLabel,
	Stack,
	Typography,
	useTheme,
} from '@mui/material';
import {BillsTaggingEnum} from 'main/analytics/gaTagsEnum';
import digitalInvoiceDrawerImg from 'presentation/assets/images/digital-invoice-drawer.webp';
import {Drawer} from 'presentation/components/Drawer';
import {ModalError} from 'presentation/components/ModalError';
import {useHomeElectronicBillDrawer} from 'presentation/hooks/useHomeElectronicBillDrawer';
import {useNewBillsBemobiFeatureFlag} from 'presentation/pages/private/hooks/useNewBillsBemobiFeatureFlag';
import Icon from 'presentation/icons';
import {useMediaQueries} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';
import React from 'react';
import {CloseButton, ContentStack, FooterStack, ImageContainer, StyledImage} from './styles';
import {ElectronicBillHomeDrawerProps} from './types';

const {
	title,
	titleBemobi,
	description,
	checkboxLabel,
	primaryButton,
	primaryButtonBemobi,
	secondaryButton,
} = translations['pt-br'].privateFlow.home.electronicBillHomeDrawer;
const {
	title: errorModalTitle,
	subtitle: errorModalSubtitle,
	description: errorModalDescription,
	tryAgainBtn,
} = translations['pt-br'].others.genericErrorModal;

const tutorialAndTooltipZIndexPlus1 = 10_101;

export function ElectronicBillHomeDrawer({dataTagId}: ElectronicBillHomeDrawerProps) {
	const {isEnabled: isBemobiFlow} = useNewBillsBemobiFeatureFlag();
	const {
		isDrawerOpen,
		isDontShowChecked,
		isSeeLaterLoading,
		isPatchError,
		handleClose,
		handleSeeLater,
		handleSubscribe,
		handleCloseError,
		toggleDontShow,
	} = useHomeElectronicBillDrawer();
	const drawerTitle = isBemobiFlow ? titleBemobi : title;
	const drawerPrimaryButton = isBemobiFlow ? primaryButtonBemobi : primaryButton;
	const {colors} = useTheme();
	const {isMobile} = useMediaQueries();

	return (
		<>
			<Drawer.Root
				open={isDrawerOpen}
				onClose={handleClose}
				onOpen={() => {}}
				customMaxHeight={'100%'}
				pullerPosition={'absolute'}
				pullerPadding={'4px 0 0 0'}
				sx={{
					'& .MuiDrawer-paper': {
						paddingTop: 0,
					},
					zIndex: tutorialAndTooltipZIndexPlus1,
				}}>
				<Drawer.Content $customPadding={'0'}>
					<ImageContainer>
						<StyledImage
							src={digitalInvoiceDrawerImg}
							alt="Uma mulher sorridente segurando o celular"
						/>
						<CloseButton
							size="small"
							onClick={handleClose}
							data-tag-id={
								dataTagId?.closeButton ??
								BillsTaggingEnum.CLICK_FECHAR_DRAWER_ADESAO_BOLETO_HOME
							}>
							<Icon name="close-default" color={colors.dark.text.primary} />
						</CloseButton>
					</ImageContainer>
					<ContentStack $isMobile={isMobile}>
						<Stack>
							<Typography variant="h6">{drawerTitle}</Typography>
							<Typography variant="body1">{description}</Typography>
						</Stack>
						<FormControlLabel
							sx={{margin: isMobile ? 0 : '0 8px'}}
							control={
								<Checkbox
									sx={{p: 0, mr: 1}}
									checked={isDontShowChecked}
									onChange={toggleDontShow}
									data-tag-id={
										dataTagId?.dontShowCheckbox ??
										BillsTaggingEnum.CLICK_NAO_MOSTRAR_90_DIAS_HOME_DRAWER
									}
								/>
							}
							label={checkboxLabel}
						/>
					</ContentStack>
				</Drawer.Content>
				<FooterStack>
					<Button
						variant="outlined"
						fullWidth
						size="large"
						disabled={isSeeLaterLoading}
						endIcon={isSeeLaterLoading ? <CircularProgress size={16} /> : undefined}
						data-tag-id={
							dataTagId?.secondaryButton ??
							BillsTaggingEnum.CLICK_VER_MAIS_TARDE_HOME_DRAWER
						}
						onClick={handleSeeLater}>
						{secondaryButton}
					</Button>
					<Button
						variant="contained"
						fullWidth
						size="large"
						data-tag-id={
							dataTagId?.primaryButton ??
							BillsTaggingEnum.CLICK_RECEBER_BOLETO_EMAIL_HOME_DRAWER
						}
						onClick={handleSubscribe}>
						{drawerPrimaryButton}
					</Button>
				</FooterStack>
			</Drawer.Root>
			<ModalError
				open={isPatchError}
				onClose={handleCloseError}
				headerTitle={errorModalTitle}
				title={errorModalSubtitle}
				description={errorModalDescription}
				firstButton={{
					title: tryAgainBtn,
					handleClick: () => {
						handleCloseError();
						void handleSeeLater();
					},
				}}
			/>
		</>
	);
}
